import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './views/Login';
import MainLayout from './components/MainLayout';
import AuthLayout from './components/AuthLayout';
import Dashboard from './views/Dashboard';
import useAuth from './hooks/useAuth';
import {
  CircularProgress,
  Backdrop,
  Box,
  Typography
} from '@mui/material';
import CreatePublication from './views/Publications/CreatePublication';
import AllPublications from './views/Publications/AllPublications';
import PublicationResult from './views/Publications/PublicationResult';
import PublicationDetail from './views/Publications/PublicationDetail';

import AllInformationPublications from './views/InformationPublications/AllInformationPublications';
import CreateInformationPublication from './views/InformationPublications/CreateInformationPublication';
import InformationPublicationDetail from './views/InformationPublications/InformationPublicationDetail';
import InformationCategories from './views/InformationPublications/Categories';

function CustomRoute({
  isPrivate,
  element,
}: {
  isPrivate?: boolean;
  element: React.ReactElement;
}) {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isPrivate && !authenticated) {
    return <Navigate to="/login" replace />;
  }

  return element;
}

const AppRoutes: React.FC = () => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route
        path="/login"
        element={
          authenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />

      <Route
        path="/dashboard"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <Dashboard />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/publicacoes"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AllPublications />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/publicacoes/nova"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <CreatePublication />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/publicacoes/resultado"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicationResult />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/publicacoes/:publicationId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <PublicationDetail />
              </MainLayout>
            }
          />
        }
      />
      
      <Route
        path="/informacoes"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <AllInformationPublications />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/informacoes/nova"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <CreateInformationPublication />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/informacoes/:informationId"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <InformationPublicationDetail />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="/informacoes/categorias"
        element={
          <CustomRoute
            isPrivate={true}
            element={
              <MainLayout>
                <InformationCategories />
              </MainLayout>
            }
          />
        }
      />

      <Route
        path="*"
        element={
          <MainLayout>
            <Box sx={{ textAlign: 'center', mt: 5 }}>
              <Typography variant="h4">404 - Página Não Encontrada</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                A página que você está procurando não existe.
              </Typography>
            </Box>
          </MainLayout>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
